/**
 * Cookies Policy Page
 *
 * Public page - accessible without authentication. Linked from the login
 * footer and used as the cookies / data-use disclosure URL for the CCMS mobile
 * apps on the Apple App Store and Google Play.
 */

import { Stack } from '@/components/ui/layout';
import { Title, Text, Anchor } from '@/components/ui/core';
import { Divider } from '@/components/ui/data-display';
import { IconCookie } from '@tabler/icons-react';
import LegalPageLayout from './LegalPageLayout';

export default function CookiesPolicyPage() {
  return (
    <LegalPageLayout
      title="Cookies Policy"
      lastUpdated="30 June 2026"
      current="cookies"
      icon={<IconCookie size={44} color="var(--mantine-color-indigo-6)" />}
    >
      {/* 1. Introduction */}
      <section>
        <Title order={3} mb="sm">1. Introduction</Title>
        <Text size="sm">
          This Cookies Policy explains how Acts Christian Church ("Acts Church", "we", "our", or
          "us") uses cookies and similar technologies in the CCMS web portal, and the equivalent
          on-device storage used by our CCMS mobile apps. It should be read together with our{' '}
          <Anchor href="/privacy">Privacy Policy</Anchor>.
        </Text>
      </section>

      <Divider />

      {/* 2. What cookies are */}
      <section>
        <Title order={3} mb="sm">2. What Are Cookies & Similar Technologies?</Title>
        <Text size="sm">
          Cookies are small text files stored on your device by your browser. We also use similar
          technologies such as browser local storage, session tokens, and device identifiers.
          Together these help the apps work, keep you signed in, and remember your preferences.
        </Text>
      </section>

      <Divider />

      {/* 3. Types we use */}
      <section>
        <Title order={3} mb="sm">3. Types We Use</Title>
        <Stack gap="xs">
          <Text size="sm">
            <strong>Strictly necessary:</strong> Required for the apps to function — for example,
            to sign you in, keep your session active, and protect security. These cannot be turned
            off.
          </Text>
          <Text size="sm">
            <strong>Preferences:</strong> Remember choices such as your theme, language, and
            accessibility settings.
          </Text>
          <Text size="sm">
            <strong>Analytics & diagnostics:</strong> Where enabled, help us understand usage and
            fix problems so we can improve the apps.
          </Text>
        </Stack>
        <Text size="sm" mt="xs">
          We do not use cookies for third-party advertising and we do not track you across other
          websites or apps for advertising purposes.
        </Text>
      </section>

      <Divider />

      {/* 4. Mobile apps */}
      <section>
        <Title order={3} mb="sm">4. Mobile Apps</Title>
        <Text size="sm">
          Our mobile apps do not use browser cookies. Instead, they use secure on-device storage
          (such as the device keychain or secure storage) to keep you signed in and to store your
          preferences, and they use push notification tokens to deliver notifications. You can clear
          this data by signing out or removing the app, and you can manage notifications in your
          device settings.
        </Text>
      </section>

      <Divider />

      {/* 5. Managing cookies */}
      <section>
        <Title order={3} mb="sm">5. Managing Cookies</Title>
        <Text size="sm">
          You can control or delete cookies through your browser settings. Please note that blocking
          strictly necessary cookies will prevent you from signing in and using the web portal.
        </Text>
      </section>

      <Divider />

      {/* 6. Changes */}
      <section>
        <Title order={3} mb="sm">6. Changes to This Policy</Title>
        <Text size="sm">
          We may update this Cookies Policy from time to time. The "Last updated" date at the top
          shows when it was last revised.
        </Text>
      </section>

      <Divider />

      {/* 7. Contact */}
      <section>
        <Title order={3} mb="sm">7. Contact Us</Title>
        <Text size="sm">
          For questions about this Cookies Policy, please contact{' '}
          <Anchor href="mailto:developer@actschurch.co.za">developer@actschurch.co.za</Anchor>.
        </Text>
      </section>
    </LegalPageLayout>
  );
}
